import React from 'react';
import { BaseComponentBuilder } from 'cv-react-core';
import Switch from '../../components/base/Switch';
import Checkbox from '../../components/base/Checkbox';
import RadioButton from '../../components/base/RadioButton';

/**
 * A builder for creating boolean style (switch) components
 */
class BooleanComponentBuilder extends BaseComponentBuilder {
    /**
     * COMPONENT BASED PROPERTIES
     */

    /** Begin Context Styles */

    setThumbStyles(thumbStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            thumb: thumbStyles,
        });
    }
    getThumbStyles() {
        const { thumb } = this.getContextStyles();
        return thumb || {};
    }

    setThumbContainerStyles(thumbContainerStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            thumbContainer: thumbContainerStyles,
        });
    }
    getThumbContainerStyles() {
        const { thumbContainer } = this.getContextStyles();
        return thumbContainer || {};
    }

    setThumbContainerSelectedStyles(thumbContainerSelectedStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            thumbContainerSelected: thumbContainerSelectedStyles,
        });
    }
    getThumbContainerSelectedStyles() {
        const { thumbContainerSelected } = this.getContextStyles();
        return thumbContainerSelected || {};
    }

    setThumbSelectedStyles(thumbSelectedStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            thumbSelected: thumbSelectedStyles,
        });
    }
    getThumbSelectedStyles() {
        const { thumbSelected } = this.getContextStyles();
        return thumbSelected || {};
    }

    setTrackStyles(trackStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            track: trackStyles,
        });
    }
    getTrackStyles() {
        const { track } = this.getContextStyles();
        return track || {};
    }

    setTrackContainerStyles(trackContainerStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            trackContainer: trackContainerStyles,
        });
    }
    getTrackContainerStyles() {
        const { trackContainer } = this.getContextStyles();
        return trackContainer || {};
    }

    setTrackSelectedStyles(trackSelectedStyles) {
        this.setContextStyles({
            ...this.getContextStyles(),
            trackSelected: trackSelectedStyles,
        });
    }
    getTrackSelectedStyles() {
        const { trackSelected } = this.getContextStyles();
        return trackSelected || {};
    }

    /** End Context Styles */


    /** Begin Props */
    setChecked(checked = true) {
        this.props.checked = checked;
        return this;
    }
    getChecked() {
        return this.props.checked;
    }

    setDisabled(disabled = true) {
        this.props.disabled = disabled;
        return this;
    }
    getDisabled() {
        return this.props.disabled;
    }

    setOnChange(onChange) {
        if (typeof onChange === 'function') {
            this.props.onChange = onChange;
        }
        return this;
    }
    getOnChange() {
        return this.props.onChange;
    }

    setShowLabels(showLabels = true) {
        this.props.showLabels = showLabels;
        return this;
    }
    getShowLabels() {
        return this.props.showLabels;
    }

    setLeftLabel(leftLabel = '') {
        this.props.leftLabel = leftLabel;
        return this;
    }
    getLeftLabel() {
        return this.props.leftLabel;
    }

    setRightLabel(rightLabel = '') {
        this.props.rightLabel = rightLabel;
        return this;
    }
    getRightLabel() {
        return this.props.rightLabel;
    }
    /** End Props */


    /**
     * PROCESS AND UPDATE COMPONENT PROPERTIES
     */

    processDisplay() {
        const stagedValue = this.getStagedValue();
        const property = this.getProperty();
        const propDef = this.getPropDef();
        const booleanValue = this.resolveBooleanValue(stagedValue, property, propDef);
        this.setChecked(booleanValue);
    }

    processChangeHandler() {
        const onChangeHandler = this.getOnValueChangeHandler();
        const isReadMode = this.getReadMode();
        const property = this.getProperty();
        const { name: propertyName = '' } = property;

        if (isReadMode || !propertyName || !onChangeHandler) {
            return;
        }

        this.setOnChange((event, props = {}) => {
            let nextVal;

            // Prefer explicit radio value
            if (Object.prototype.hasOwnProperty.call(props, 'value')) {
            nextVal = props.value;
            }
            else if (Object.prototype.hasOwnProperty.call(props, 'checked')) {
            // Instead of `!props.checked` with ternary
            if (Object.prototype.hasOwnProperty.call(props, 'value')) {
                nextVal = props.value;
                }
                else if (Object.prototype.hasOwnProperty.call(props, 'checked')) {
                nextVal = !props.checked; // simpler, lint-safe
                }
            }

            if (Object.prototype.hasOwnProperty.call(props, 'checkedValue')) {
            if (nextVal) {
                onChangeHandler(propertyName, props.checkedValue);
            }
            }
            else {
            onChangeHandler(propertyName, nextVal);
            }
        });
    }

    processStyle() {
        const categorizedStyles = BaseComponentBuilder.styleHelper.categorizeWebStyles(this.getStyle());
        const {
            container,
            text,
        } = categorizedStyles;

        this.setContainerStyles({
            ...container,
            ...this.getContainerStyles(),
        });
        this.setThumbStyles({
            color: text.color,
            ...this.getThumbStyles(),
        });
    }

    buildProps() {
        // Update the selected state
        this.processDisplay();

        // Update change handler to support property / value data
        this.processChangeHandler();

        // Update the input style
        this.processStyle();
    }


    /**
     * BUILD AND RETURN COMPONENT AS REACT ELEMENT
     */

    build() {
        this.buildProps();
        const entryMethod = this.data?.viewDef?.entryMethod ?? '';
        const labelOn = this.data?.viewDef?.onLabel ?? this.data.propDef.booleanTrueAlias;
        const labelOff = this.data?.viewDef?.offLabel ?? this.data.propDef.booleanFalseAlias;

        switch (entryMethod) {
            case 'ENTRY_METHOD_TOGGLE_WITH_LABELS':
            return (
                <Switch
                    { ...this.getProps() }
                    showLabels
                    onLabel={ labelOn }
                    offLabel={ labelOff }
                    labelPosition="sides" />
            );
            case 'ENTRY_METHOD_TOGGLE':
            return <Switch { ...this.getProps() } />;
            case 'ENTRY_METHOD_CHECKBOX':
            return <Checkbox { ...this.getProps() } />;
            case 'ENTRY_METHOD_RADIO_BUTTONS':
            return (
                <RadioButton
                    { ...this.getProps() }
                    onLabel={ labelOn }
                    offLabel={ labelOff } />
);
            default:
            return <Switch { ...this.getProps() } />;
        }
    }
}

export default BooleanComponentBuilder;
